import React, { useMemo } from 'react'
import moment from 'moment-timezone'
import { map } from 'lodash'
import { makeStyles } from '@material-ui/core'
import { CellProps, ColumnInstance } from 'react-table'

import { useFeatureEnabled } from 'civic-champs-shared/feature/hooks'
import {
  ContactCell,
  ContactHeader,
  contactSort,
  ExpandableCell,
  NameCell,
  PersonLinkCell,
} from 'core/table/cells/modern'
import { FIRST_LAST_NAME, GROUPS, TAGS } from 'champion/utils/filters'
import { DYNAMIC_TEXT } from 'core/table/filters'
import { createStringSort, createTagsSort } from 'core/table/utils'
import { NullableTextCell, PhoneNumberCell, WithLinkCell } from 'core/table/cells'
import { filterByRequireRules } from 'civic-champs-shared/utils/filterByRequireRules'
import { createDateValueSort } from 'components/table/sort'
import { useGroups } from 'group/hooks/useGroups'
import { useTagsCollection } from 'tags/hooks'
import Tags from 'components/tag/Tags'
import { Tag } from 'new-mentorship/types'
import { ARRAY_OPERATOR_OPTIONS, STRING_OPERATOR_OPTIONS } from 'core/table/interfaces/Filters'
import { CheckIn } from '../types'
import { useUISettings } from 'hooks/useUISettings'
import { CheckInTimeCell } from '../components/cells'

//TODO this comes from `useChampionColumns.tsx`
export const useStyles = makeStyles(theme => ({
  error: {
    color: theme.palette.error.main,
  },
  link: {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    cursor: 'pointer',
  },
  selectColumn: {
    '&>div': {
      width: '46px',
    },
  },
  idColumn: {
    '&>div': {
      width: '70px',
    },
  },
  nameColumn: {
    '&>div': {
      width: '160px',
    },
  },
  firstNameColumn: {
    '&>div': {
      width: '90px',
    },
  },
  lastNameColumn: {
    '&>div': {
      width: '110px',
    },
  },
  contactColumn: {
    '&>div': {
      width: '100px',
    },
  },
}))

export const getOperatorOptions = (column: string) => {
  switch (column) {
    case 'firstName':
    case 'lastName':
    case 'email':
    case 'mobile':
    case 'name':
    case 'homePhoneNumber':
    case 'opportunity':
      return STRING_OPERATOR_OPTIONS

    case 'groups':
    case 'tags':
      return ARRAY_OPERATOR_OPTIONS

    default:
      return []
  }
}

export function useCheckInsColumns() {
  const tagsEnabled = useFeatureEnabled('ChampionsTags')

  const classes = useStyles()

  // @ts-ignore
  const { showAdminItems } = useUISettings()
  const { groups } = useGroups()
  const [{ tags }] = useTagsCollection()

  return useMemo(() => {
    const OpportunityCell = (props: any) => {
      if (props.row.original.opportunity) {
        return WithLinkCell(
          NullableTextCell,
          (row: CheckIn) => `/events/${row.opportunity.encodedId}/${row.organizationId}`,
          // @ts-ignore
          null,
        )({ ...props, linkProps: { className: classes.link } })
      }

      return <span className={classes.error}>Cancelled</span>
    }

    return filterByRequireRules(
      [
        {
          id: 'id',
          Header: 'Id',
          accessor: 'user.id',
          disableFilters: true,
          className: classes.idColumn,
          requires: 'showAdminItems',
        },
        {
          id: 'name',
          Header: 'Name',
          accessor: 'user',
          Cell: PersonLinkCell,
          filter: FIRST_LAST_NAME,
          sortType: contactSort,
          className: classes.nameColumn,
        },
        {
          id: 'firstName',
          Header: 'First Name',
          accessor: 'user.givenName',
          Cell: NameCell,
          className: classes.firstNameColumn,
          filter: DYNAMIC_TEXT,
        },
        {
          id: 'lastName',
          Header: 'Last Name',
          accessor: 'user.familyName',
          Cell: NameCell,
          className: classes.lastNameColumn,
          filter: DYNAMIC_TEXT,
        },
        {
          id: 'contact',
          Header: <ContactHeader />,
          accessor: 'user',
          Cell: ContactCell,
          disableSortBy: true,
          disableFilters: true,
          className: classes.contactColumn,
        },
        {
          id: 'email',
          Header: 'Email',
          accessor: 'user.email',
          filter: DYNAMIC_TEXT,
          className: classes.emailColumn,
          sortType: createStringSort('email'),
        },
        {
          id: 'mobile',
          Header: 'Mobile Phone',
          accessor: 'user.phoneNumber',
          Cell: PhoneNumberCell,
          filter: DYNAMIC_TEXT,
          className: classes.mobileColumn,
          sortType: createStringSort('mobile'),
        },
        {
          id: 'homePhoneNumber',
          Header: 'Home Phone',
          accessor: 'user.homePhoneNumber',
          className: classes.homeColumn,
          Cell: PhoneNumberCell,
          filter: DYNAMIC_TEXT,
          sortType: createStringSort('homePhoneNumber'),
        },
        {
          id: 'opportunity',
          Header: 'Opportunity',
          accessor: 'opportunity.name',
          Cell: OpportunityCell,
          filter: DYNAMIC_TEXT,
          className: classes.opportunityColumn,
          sortType: createStringSort('opportunity.name'),
        },
        {
          id: 'checkInTime',
          Header: 'Check-In Time',
          accessor: 'checkedInAt',
          Cell: CheckInTimeCell,
          disableFilters: true,
          sortType: createDateValueSort('checkedInAt'),
        },
        {
          id: 'forceCheckOutTime',
          Header: 'Force Check-Out Time',
          accessor: 'forceCheckOutAt',
          Cell: CheckInTimeCell,
          disableFilters: true,
          sortType: createDateValueSort('forceCheckOutAt'),
          requires: 'showAdminItems',
        },
        //TODO checkout time?
        {
          id: 'groups',
          Header: 'Groups',
          accessor: 'user.groups',
          Cell: ExpandableCell,
          filter: GROUPS,
          className: classes.groupsColumn,
          sortType: createStringSort('groups'),
          getFilterAutocomplete: (): string[] => map(groups, 'name'),
        },
        {
          id: 'tags',
          Header: 'Tags',
          accessor: 'user.tags',
          filter: TAGS,
          Cell: ({ cell: { value } }: CellProps<any>) => (
            <Tags expandable tags={(value || []).map(({ name: label, id, color }: Tag) => ({ label, id, color }))} />
          ),
          className: classes.tagsColumn,
          getFilterAutocomplete: () => tags.map(({ id, name, color }) => ({ label: name, value: id, color })),
          sortType: createTagsSort('tags'),
          isTags: true,
          requires: 'tagsEnabled',
        },
      ],
      {
        showAdminItems,
        tagsEnabled,
      },
    ) as any as ColumnInstance<CheckIn>[]
  }, [
    classes.idColumn,
    classes.nameColumn,
    classes.firstNameColumn,
    classes.lastNameColumn,
    classes.contactColumn,
    classes.emailColumn,
    classes.mobileColumn,
    classes.homeColumn,
    classes.opportunityColumn,
    classes.groupsColumn,
    classes.tagsColumn,
    classes.error,
    classes.link,
    showAdminItems,
    tagsEnabled,
    groups,
    tags,
  ])
}
